import type { AppointmentType } from '@mend/types/appointment';
import type { AutocompleteProps } from '@mui/material';
import type { BaseAutocompleteInputProps } from './Autocomplete.types.ts';

import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { matchSorter } from '#lib/match-sorter.ts';
import { appointmentTypes as getAppointmentTypesQueryOptions } from '#lib/react-query/queries.ts';
import { getTextFieldProps } from './Autocomplete.utils.ts';

const defaultData: AppointmentType[] = [];

const defaultPreFilterOptions = (options: AppointmentType[]) => options;

// Updated in insights
const getOptionLabel = (appointmentType: AppointmentType) =>
  `${appointmentType.name} - ${appointmentType.minutes} minutes`;

interface AppointmentTypesAutocompleteProps<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
> extends Omit<
      AutocompleteProps<AppointmentType, Multiple, DisableClearable, false>,
      | 'loading'
      | 'options'
      | 'getOptionLabel'
      | 'filterOptions'
      | 'isOptionEqualToValue'
      | 'onInputChange'
      | 'renderInput'
      | 'renderOption'
      | 'renderTags'
      | 'freeSolo'
    >,
    // Omit added in insights
    Omit<BaseAutocompleteInputProps<AppointmentType>, 'dataFilter'> {
  /**
   * Pre-filter the options before the input based filter is applied.
   */
  preFilterOptions?: (options: AppointmentType[]) => AppointmentType[];
}

export function AppointmentTypesAutocomplete<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
>({
  inputName = 'appointment-type',
  inputLabel = 'Appointment Type',
  inputHelperText,
  inputError,
  inputRequired,
  inputSize = 'small',
  fullWidth = true,
  filterSelectedOptions = true,
  clearOnBlur = false,
  selectOnFocus = true,
  handleHomeEndKeys = true,
  multiple,
  preFilterOptions = defaultPreFilterOptions,
  ...props
}: AppointmentTypesAutocompleteProps<Multiple, DisableClearable>): JSX.Element {
  const query = useQuery(getAppointmentTypesQueryOptions());

  return (
    <Autocomplete
      freeSolo={false}
      multiple={multiple}
      fullWidth={fullWidth}
      clearOnBlur={clearOnBlur}
      selectOnFocus={selectOnFocus}
      handleHomeEndKeys={handleHomeEndKeys}
      loading={query.isLoading}
      options={query.data ?? defaultData}
      getOptionLabel={getOptionLabel}
      filterSelectedOptions={filterSelectedOptions}
      filterOptions={(options, { inputValue }) =>
        matchSorter(preFilterOptions(options), inputValue, { keys: ['name'] })
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {getOptionLabel(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...getTextFieldProps({ params, multiple, inputRequired })}
          name={inputName}
          label={inputLabel}
          helperText={inputHelperText}
          error={inputError}
          size={inputSize}
        />
      )}
      {...props}
    />
  );
}
